/**
 * NotificationProvider — Global context provider for the enterprise notification system.
 * Subscribes to the notification service and manages notification state.
 * Renders the NotificationCenter overlay.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Notification } from "../../services/notification.types";
import { subscribe, onDismiss as onDismissService } from "../../services/notification.service";
import NotificationCenter from "./NotificationCenter";

interface NotificationContextValue {
 notifications: Notification[];
 dismissAll: () => void;
 clearHistory: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
 notifications: [],
 dismissAll: () => {},
 clearHistory: () => {},
});

export function useNotificationCenter() {
 return useContext(NotificationContext);
}

const MAX_HISTORY = 50;

export default function NotificationProvider({ children }: { children: React.ReactNode }) {
 const [notifications, setNotifications] = useState<Notification[]>([]);
 const mountedRef = useRef(true);

 useEffect(() => {
 mountedRef.current = true;
 return () => {
 mountedRef.current = false;
 };
 }, []);

 // Subscribe to new notifications
 useEffect(() => {
 const unsubscribe = subscribe((notification) => {
 if (!mountedRef.current) return;
 setNotifications((prev) => {
 const next = [notification, ...prev];
 // Keep only MAX_HISTORY items
 return next.slice(0, MAX_HISTORY);
 });
 });
 return unsubscribe;
 }, []);

 // Subscribe to dismiss requests from service
 useEffect(() => {
 const unsubscribe = onDismissService((id) => {
 handleDismiss(id);
 });
 return unsubscribe;
 }, []);

 const handleDismiss = useCallback((id: string) => {
 setNotifications((prev) =>
 prev.map((n) => (n.id === id ? { ...n, isDismissed: true } : n))
 );

 // Remove from list after animation completes
 setTimeout(() => {
 if (!mountedRef.current) return;
 setNotifications((prev) => prev.filter((n) => n.id !== id));
 }, 400);
 }, []);

 const dismissAll = useCallback(() => {
 setNotifications((prev) => prev.map((n) => ({ ...n, isDismissed: true })));
 setTimeout(() => {
 if (!mountedRef.current) return;
 setNotifications([]);
 }, 400);
 }, []);

 const clearHistory = useCallback(() => {
 setNotifications([]);
 }, []);

 return (
 <NotificationContext.Provider value={{ notifications, dismissAll, clearHistory }}>
 {children}
 <NotificationCenter notifications={notifications} onDismiss={handleDismiss} />
 </NotificationContext.Provider>
 );
}
